/* eslint-disable @stylistic/max-len */
import {type RefObject} from 'react';
import React from 'react';

interface ChevronDownProps {
    /**
     * A string representing the CSS class to be applied to the ChevronDownIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the ChevronDownIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the ChevronDownIcon element.
     */
    width?: number | string;
}

/**
 * The `ChevronDown` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `ChevronDownProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the ChevronDownIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the ChevronDownIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the ChevronDownIcon element.
 * @param props.ref       A ref to the SVG element.
 * @returns A React element representing the `ChevronDown` component with the specified properties.
 *
 * @example
 * ```tsx
 * const ChevronDownComponent = <ChevronDown className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const ChevronDown = ({
    className = '',
    color1 = '#0019A5',
    height = 24,
    ref,
    testId = 'ChevronDown',
    width = 24
}: ChevronDownProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M4.47 8.47c.3-.3.77-.3 1.06 0L12 14.94l6.47-6.47a.75.75 0 1 1 1.06 1.06l-7 7c-.3.3-.77.3-1.06 0l-7-7a.75.75 0 0 1 0-1.06Z" fill={color1} fillRule="evenodd" />
    </svg>
);

ChevronDown.displayName = 'ChevronDown';